import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ContractForm } from '../components/ContractForm';
import { useContract, useCreateContract, useUpdateContract } from '../queries';
import type { ContractInput } from '../types';

export function ContractFormPage() {
  const { publicId = '' } = useParams();
  const isEdit = Boolean(publicId);
  const contractId = publicId;
  const navigate = useNavigate();

  const contractQuery = useContract(isEdit ? contractId : '');
  const create = useCreateContract();
  const update = useUpdateContract(contractId);

  async function handleSubmit(values: ContractInput) {
    const saved = isEdit ? await update.mutateAsync(values) : await create.mutateAsync(values);
    navigate(`/crm/contracts/${saved.public_id}`);
  }

  if (isEdit && contractQuery.isPending) {
    return (
      <div className="max-w-xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  const contract = contractQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(isEdit ? `/crm/contracts/${contractId}` : '/crm/contracts')}
        >
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? `Edit ${contract?.title ?? 'contract'}` : 'New contract'}
        </h1>
      </div>

      <ContractForm
        submitLabel={isEdit ? 'Save changes' : 'Create contract'}
        defaultValues={
          contract
            ? {
                title: contract.title,
                company: String(contract.company),
                deal: contract.deal ? String(contract.deal) : '',
                project: contract.project ? String(contract.project) : '',
                status: contract.status,
                currency: contract.currency ?? 'USD',
                amount: contract.amount ?? '',
                starts_on: contract.starts_on ?? '',
                ends_on: contract.ends_on ?? '',
                notes: contract.notes,
              }
            : undefined
        }
        onSubmit={handleSubmit}
        onCancel={() => navigate(isEdit ? `/crm/contracts/${contractId}` : '/crm/contracts')}
      />
    </div>
  );
}
